/**
 * AdminHubNav — shared tab row that sits at the top of each admin page so
 * an admin can hop between Beta · Activity · Feedback in one click without
 * digging through the navbar overflow menu.
 *
 * Pass `active` = "beta" | "activity" | "feedback" to highlight the current
 * page. Renders nothing for non-admin viewers (the parent already guards
 * routes at the API/router layer, this is a defensive UI no-op).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Activity, MessageSquare, Briefcase, Store, Star, FileSignature, Megaphone, Sparkles, Loader } from "lucide-react";
import { api } from "../lib/api";

const TABS = [
  { id: "beta",      to: "/admin/beta-invites", label: "Beta Invites",     icon: ShieldCheck,   testid: "admin-hub-beta" },
  { id: "early-access", to: "/admin/early-access", label: "Early Access",  icon: Megaphone,     testid: "admin-hub-early-access" },
  { id: "nda",       to: "/admin/nda",          label: "NDA",              icon: FileSignature, testid: "admin-hub-nda" },
  { id: "activity",  to: "/admin/activity",     label: "Activity",         icon: Activity,      testid: "admin-hub-activity" },
  { id: "feedback",  to: "/admin/feedback",     label: "Feedback",         icon: MessageSquare, testid: "admin-hub-feedback" },
  { id: "employers", to: "/admin/employers",    label: "Employers",        icon: Briefcase,     testid: "admin-hub-employers" },
  // Cross-links into the employer surfaces so admins can inspect the
  // Candidates Store + their own Shortlist without swapping accounts. Backend
  // resolves the admin candidate JWT into a "shadow employer" so every
  // employer endpoint just works.
  { id: "store",     to: "/employer/store",     label: "Candidates Store", icon: Store,         testid: "admin-hub-store" },
  { id: "shortlist", to: "/employer/shortlist", label: "Shortlist",        icon: Star,          testid: "admin-hub-shortlist" },
];

export default function AdminHubNav({ active }) {
  const [obEnabled, setObEnabled] = useState(null);
  const [obBusy, setObBusy] = useState(false);

  useEffect(() => {
    api.get("/onboarding/config")
      .then((r) => setObEnabled(r.data?.enabled !== false))
      .catch(() => setObEnabled(true));
  }, []);

  const toggleOnboarding = async () => {
    if (obBusy || obEnabled === null) return;
    setObBusy(true);
    try {
      const r = await api.post("/admin/onboarding-config", { enabled: !obEnabled });
      setObEnabled(r.data?.enabled);
    } catch { /* keep current state */ }
    setObBusy(false);
  };

  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-hub-nav">
      <div
        className="rm-tag text-[10px]"
        style={{ background: "#FFC145", borderColor: "#0F1B2D", color: "#0F1B2D" }}
      >
        Admin
      </div>
      {TABS.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.id;
        return (
          <Link
            key={t.id}
            to={t.to}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-[11px] font-mono-rm transition-colors"
            style={{
              background: isActive ? "#0F1B2D" : "white",
              color: isActive ? "#FFC145" : "#0F1B2D",
              borderColor: "#0F1B2D",
              fontWeight: 700,
            }}
            data-testid={t.testid}
          >
            <Icon size={13} />
            {t.label}
          </Link>
        );
      })}
      <button
        onClick={toggleOnboarding}
        disabled={obBusy || obEnabled === null}
        title="Turn the guided new-user onboarding on or off instantly"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-[11px] font-mono-rm transition-colors"
        style={{
          background: obEnabled ? "#0E5A4A" : "#7A1F1F",
          color: "white",
          borderColor: "#0F1B2D",
          fontWeight: 700,
          cursor: obBusy ? "wait" : "pointer",
          opacity: obEnabled === null ? 0.6 : 1,
        }}
        data-testid="admin-onboarding-toggle"
      >
        {obBusy ? <Loader size={13} className="animate-spin" /> : <Sparkles size={13} />}
        Onboarding: {obEnabled === null ? "…" : obEnabled ? "ON" : "OFF"}
      </button>
    </div>
  );
}
